/*
 * Standalone coastal weather. One optional same-store cloud asset; no dependencies.
 * Inputs on .coast-scene:
 *   coast:time { phase, night, mode, warm?, still? }
 *   coast:lamp { age, strike, lamp, starting, reduced }
 *   coast:pause { paused, userInitiated? }, coast:motion { playing, state }
 * Emits coast:weather { storm, rain, flash } for synchronised scenery lighting.
 * Clouds/rain sit behind the card; only a narrow bolt reaches the measured logo.
 */
(() => {
  'use strict';
  if (window.CgauCoastalWeather) { window.CgauCoastalWeather.scan(document); return; }
  const registry = new Map();
  const TAU = Math.PI * 2;
  const clamp = (n, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, n));
  const finite = (n, fallback) => Number.isFinite(Number(n)) && n !== '' && n != null ? Number(n) : fallback;
  const smooth = (lo, hi, n) => { const v = clamp((n - lo) / (hi - lo)); return v * v * (3 - 2 * v); };
  const hash = n => { const v = Math.sin(n * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
  const RAIN_LAYERS = [
    {speed: .58, length: .66, alpha: 1.10, width: .66, rgb: '163,182,205'},
    {speed: .91, length: .96, alpha: 1.85, width: .88, rgb: '177,196,216'},
    {speed: 1.38, length: 1.30, alpha: 1.25, width: 1.14, rgb: '193,207,230'}
  ];
  let cloudCache;
  const photographicClouds = new Map();

  // Decode and shade the photographic sprite once. Runtime drawing is only a
  // few cached bitmap composites, never a volumetric simulation or pixel loop.
  function loadCloud(url) {
    if (!url || typeof Image === 'undefined') return null;
    if (!photographicClouds.has(url)) photographicClouds.set(url, new Promise(resolve => {
      const image = new Image(); image.decoding = 'async';
      image.onload = () => {
        try {
          const shade = (colour, alpha) => {
            const tile = document.createElement('canvas');
            tile.width = image.naturalWidth; tile.height = image.naturalHeight;
            const context = tile.getContext('2d');
            if (!context) return image;
            context.drawImage(image, 0, 0);
            context.globalCompositeOperation = 'source-atop';
            context.globalAlpha = alpha; context.fillStyle = colour;
            context.fillRect(0, 0, tile.width, tile.height);
            return tile;
          };
          resolve({storm: shade('#10182c', .48), lit: shade('#b8b6ff', .13), photographic: true});
        } catch (_) { resolve(null); }
        image.onload = image.onerror = null;
      };
      image.onerror = () => { image.onload = image.onerror = null; resolve(null); };
      image.src = url;
    }));
    return photographicClouds.get(url);
  }

  // A small cached density/lighting field makes soft, irregular billows. This
  // is generated once; the animation only translates/composites its bitmaps.
  function cloudTexture(storm) {
    const tile = document.createElement('canvas');
    tile.width = 680; tile.height = 300;
    const ctx = tile.getContext('2d');
    if (!ctx) return tile;
    const pixels = ctx.createImageData(tile.width, tile.height);
    const blobs = Array.from({length: 13}, (_, i) => ({
      x: .10 + i * .065 + (hash(i + 33) - .5) * .05,
      y: .51 - Math.sin(i / 12 * Math.PI) * .11 + (hash(i + 77) - .5) * .15,
      rx: .12 + hash(i + 113) * .075,
      ry: .20 + hash(i + 171) * .17
    }));
    const grain = new Float32Array(64 * 64);
    for (let i = 0; i < grain.length; i++) grain[i] = hash(i + 887);
    const noise = (x, y) => {
      const ix = Math.floor(x), iy = Math.floor(y);
      let tx = x - ix, ty = y - iy;
      tx = tx * tx * (3 - 2 * tx); ty = ty * ty * (3 - 2 * ty);
      const at = (xx, yy) => grain[((yy & 63) * 64) + (xx & 63)];
      const a = at(ix, iy) * (1 - tx) + at(ix + 1, iy) * tx;
      const b = at(ix, iy + 1) * (1 - tx) + at(ix + 1, iy + 1) * tx;
      return a * (1 - ty) + b * ty;
    };
    const fbm = (x, y) => noise(x, y) * .55 + noise(x * 2.03 + 13, y * 2.03 + 7) * .27
      + noise(x * 4.11 + 31, y * 4.11 + 19) * .13 + noise(x * 8.31 + 3, y * 8.31 + 37) * .05;
    const field = new Float32Array(tile.width * tile.height);
    // A summed, warped density field has continuous derivatives. In particular,
    // no individual billow owns a surface normal at an intersecting boundary.
    for (let py = 0; py < tile.height; py++) {
      for (let px = 0; px < tile.width; px++) {
        const x = px / tile.width, y = py / tile.height;
        const wx = x + (noise(x * 7 + 17, y * 5 + 11) - .5) * .060;
        const wy = y + (noise(x * 8 + 41, y * 6 + 29) - .5) * .100;
        let envelope = 0;
        for (const blob of blobs) {
          const dx = (wx - blob.x) / blob.rx, dy = (wy - blob.y) / blob.ry;
          envelope += Math.exp(-(dx * dx + dy * dy) * 2.2) * .52;
        }
        const detail = fbm(x * 18, y * 11);
        const volume = envelope * (.66 + detail * .57) + (detail - .5) * .13;
        const border = smooth(0, .065, x) * (1 - smooth(.935, 1, x))
          * smooth(.02, .13, y) * (1 - smooth(.80, .98, y));
        field[py * tile.width + px] = smooth(.085, .57, volume) * border;
      }
    }
    const atField = (x, y) => field[clamp(y, 0, tile.height - 1) * tile.width + clamp(x, 0, tile.width - 1)];
    for (let py = 0; py < tile.height; py++) {
      for (let px = 0; px < tile.width; px++) {
        const density = field[py * tile.width + px];
        if (density < .003) continue;
        const x = px / tile.width, y = py / tile.height;
        const detail = fbm(x * 20 + 3, y * 14 + 7);
        const slope = (atField(px - 7, py - 5) - atField(px + 7, py + 5)) * .19;
        const light = clamp(.57 - y * .19 + detail * .21 - slope);
        const lower = smooth(.52, .88, y);
        const base = storm ? [23, 27, 43] : [145, 154, 163];
        const lift = storm ? [47, 47, 63] : [116, 105, 92];
        const at = (py * tile.width + px) * 4;
        for (let channel = 0; channel < 3; channel++) {
          pixels.data[at + channel] = base[channel] + lift[channel] * light
            + (storm ? [11, 4, -3][channel] * lower : 0);
        }
        pixels.data[at + 3] = density * (storm ? 196 : 180);
      }
    }
    ctx.putImageData(pixels, 0, 0);
    return tile;
  }

  // One small, fully feathered rain-haze sprite. Its outer pixels are exactly
  // transparent on all four sides; every runtime composite can cross another
  // without revealing a rectangular fill boundary above or below the water.
  function mistTexture() {
    const tile = document.createElement('canvas');
    tile.width = 256; tile.height = 192;
    const ctx = tile.getContext('2d');
    if (!ctx) return tile;
    const pixels = ctx.createImageData(tile.width, tile.height);
    for (let py = 0; py < tile.height; py++) {
      for (let px = 0; px < tile.width; px++) {
        const x = px / (tile.width - 1), y = py / (tile.height - 1);
        const warp = Math.sin(x * 9 + y * 5) * .08 + Math.sin(x * 17 - y * 11) * .025;
        const dx = (x - .5) / .5, dy = (y - .5 + warp) / .5;
        const edge = smooth(0, .17, x) * (1 - smooth(.83, 1, x))
          * smooth(0, .19, y) * (1 - smooth(.78, 1, y));
        const wisps = .82 + Math.sin(x * 19 + y * 7) * .10 + Math.sin(x * 8 - y * 14) * .08;
        const density = Math.exp(-(dx * dx * 1.5 + dy * dy * 2.2)) * edge * wisps;
        const at = (py * tile.width + px) * 4;
        pixels.data[at] = 104; pixels.data[at + 1] = 124; pixels.data[at + 2] = 145;
        pixels.data[at + 3] = density * 166;
      }
    }
    ctx.putImageData(pixels, 0, 0);
    return tile;
  }

  function textures() {
    if (!cloudCache) cloudCache = {day: cloudTexture(false), storm: cloudTexture(true), mist: mistTexture()};
    return cloudCache;
  }

  class CoastalWeather {
    constructor(section) {
      this.section = section;
      this.scene = section.querySelector('.coast-scene');
      if (!this.scene) return;
      this.card = section.querySelector('.newsletter-card');
      this.action = section.querySelector('.newsletter-action');
      this.sign = section.querySelector('.watchlist-logo') || section.querySelector('.watchlist-sign');
      this.canvas = this.createCanvas('coastal-weather-canvas', 'atmosphere');
      this.lightning = this.createCanvas('coastal-lightning-canvas', 'lightning');
      this.ctx = this.canvas.getContext('2d', {alpha: true});
      this.boltCtx = this.lightning.getContext('2d', {alpha: true});
      if (!this.ctx || !this.boltCtx) { this.canvas.remove(); this.lightning.remove(); return; }
      this.clouds = textures();
      this.cleanups = [];
      this.frame = 0; this.lastStamp = 0; this.lastDraw = 0;
      this.elapsed = 0; this.frames = 0;
      this.destroyed = false; this.suspended = false;
      this.userPaused = section.dataset.paused === 'true';
      this.ambientPaused = section.dataset.cgauMotionPaused === 'true';
      this.motionOverride = false;
      this.rendererPaused = ['paused', 'ambient-paused', 'hidden', 'reduced'].includes(this.scene.dataset.motion);
      this.rendererSuspended = ['ambient-paused', 'hidden'].includes(this.scene.dataset.motion);
      this.preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.phase = clamp(finite(this.scene.dataset.phase, .25));
      this.night = clamp(finite(this.scene.dataset.night, 0));
      this.mode = section.dataset.mode || 'day';
      this.lamp = {age: 0, strike: 0, lamp: 0, starting: false, reduced: false};
      this.pendingStill = true;
      this.lastWeather = '';
      const bounds = section.getBoundingClientRect();
      this.visible = bounds.bottom > 0 && bounds.top < innerHeight && bounds.right > 0 && bounds.left < innerWidth;
      // Fixed seeds and eighteen reusable depth/layer buckets give a dense
      // downpour without one Canvas stroke call per droplet.
      this.drops = Array.from({length: 960}, (_, i) => ({
        x: hash(i + 103), y: hash(i + 719), speed: 310 + hash(i + 431) * 470,
        length: 17 + hash(i + 877) * 20, layer: i % 10 < 5 ? 0 : i % 10 < 9 ? 1 : 2,
        lean: .17 + hash(i + 1549) * .09
      }));
      this.rainBuckets = Array.from({length: 18}, () => []);
      this.impacts = Array.from({length: 64}, (_, i) => ({
        x: hash(i + 1771), depth: hash(i + 2017), phase: hash(i + 2399), speed: 1.3 + hash(i + 2707) * 1.1
      }));
      this.tick = this.tick.bind(this);
      this.listen(this.scene, 'coast:time', event => {
        const nextMode = event.detail?.mode || this.mode;
        const nextPhase = clamp(finite(event.detail?.phase, this.phase));
        const nextNight = clamp(finite(event.detail?.night, this.night));
        const modeChanged = nextMode !== this.mode;
        if (nextPhase !== this.phase || nextNight !== this.night || modeChanged) this.pendingStill = true;
        this.phase = nextPhase; this.night = nextNight; this.mode = nextMode;
        if ((modeChanged || event.detail?.still === true) && !this.running()) this.paint();
      });
      this.listen(this.scene, 'coast:lamp', event => {
        const value = event.detail || {};
        const next = {age: finite(value.age, this.lamp.age), strike: clamp(finite(value.strike, 0)),
          lamp: clamp(finite(value.lamp, 0)), starting: value.starting === true, reduced: value.reduced === true};
        if (next.strike !== this.lamp.strike || next.starting !== this.lamp.starting || next.reduced !== this.lamp.reduced
          || (next.age < 1.22) !== (this.lamp.age < 1.22)) this.pendingStill = true;
        this.lamp = next;
        // A paused/reduced input can choose one still; hidden/ambient/offscreen
        // inputs are retained without touching either canvas.
        if (this.pendingStill && !this.running()) this.paint();
      });
      this.listen(this.scene, 'coast:pause', event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.userPaused = event.detail.paused;
        if (this.userPaused) this.motionOverride = false;
        else if (event.detail.userInitiated === true) this.motionOverride = true;
        this.sync();
      });
      this.listen(this.scene, 'coast:motion', event => {
        const state = event.detail?.state;
        // This effect covers the whole section, including the sign above the
        // mobile scene. The water child's offscreen state is not our viewport.
        this.rendererPaused = ['paused', 'ambient-paused', 'hidden', 'reduced'].includes(state);
        this.rendererSuspended = ['ambient-paused', 'hidden'].includes(state);
        this.sync();
      });
      const ambient = event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.ambientPaused = event.detail.paused; this.sync();
      };
      this.listen(section, 'cgau:ambient-motion-change', ambient);
      this.listen(window, 'cgau:ambient-motion-change', event => { if (event.target === window) ambient(event); });
      this.listen(document, 'visibilitychange', () => this.sync());
      this.listen(this.preference, 'change', () => {
        this.motionOverride = false; this.pendingStill = true; this.sync();
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }, {threshold: 0});
        this.io.observe(section);
      }
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.resize());
        for (const item of [section, this.scene, this.card, this.sign]) if (item) this.ro.observe(item);
      } else this.listen(window, 'resize', () => this.resize());
      this.resize(); this.sync();
      loadCloud(this.scene.dataset.weatherCloud)?.then(cloud => {
        if (!cloud || this.destroyed) return;
        this.clouds = {...this.clouds, ...cloud}; this.pendingStill = true;
        // Loading below the viewport must not wake either animation canvas.
        if (this.drawable()) this.paint();
      });
    }

    createCanvas(className, layer) {
      const canvas = document.createElement('canvas');
      canvas.className = className; canvas.dataset.weatherLayer = layer;
      canvas.setAttribute('aria-hidden', 'true'); canvas.setAttribute('role', 'presentation');
      this.section.append(canvas); return canvas;
    }
    listen(target, type, callback) {
      target.addEventListener(type, callback);
      this.cleanups.push(() => target.removeEventListener(type, callback));
    }
    reduced() { return this.preference.matches && !this.motionOverride; }
    drawable() {
      return !this.destroyed && !this.suspended && !document.hidden && this.visible &&
        !this.ambientPaused && !this.rendererSuspended && this.width > 0;
    }
    running() { return this.drawable() && !this.userPaused && !this.rendererPaused && !this.reduced(); }
    sync() {
      if (this.destroyed) return;
      this.section.dataset.weatherState = this.suspended || document.hidden ? 'hidden' : !this.visible ? 'offscreen' :
        this.ambientPaused || this.rendererSuspended ? 'ambient-paused' : this.reduced() ? 'reduced' :
          this.userPaused || this.rendererPaused ? 'paused' : 'running';
      if (!this.running()) { cancelAnimationFrame(this.frame); this.frame = 0; this.lastStamp = 0; }
      else if (!this.frame) { this.lastStamp = 0; this.frame = requestAnimationFrame(this.tick); }
      if (this.pendingStill && this.drawable()) this.paint();
    }
    tick(stamp) {
      this.frame = 0;
      if (!this.running()) { this.sync(); return; }
      if (this.lastStamp) this.elapsed += Math.min(.08, (stamp - this.lastStamp) / 1000);
      this.lastStamp = stamp;
      if (stamp - this.lastDraw >= 1000 / 30 - .4) { this.lastDraw = stamp; this.paint(); }
      this.frame = requestAnimationFrame(this.tick);
    }
    relative(node, section) {
      if (!node) return null;
      const box = node.getBoundingClientRect();
      return {x: box.left - section.left, y: box.top - section.top, width: box.width, height: box.height,
        right: box.right - section.left, bottom: box.bottom - section.top};
    }
    resize() {
      if (this.destroyed) return;
      const section = this.section.getBoundingClientRect();
      if (!section.width || !section.height) return;
      this.width = section.width; this.height = section.height;
      this.dpr = Math.min(devicePixelRatio || 1, 1.5, Math.sqrt(1700000 / (this.width * this.height)));
      for (const canvas of [this.canvas, this.lightning]) {
        canvas.width = Math.round(this.width * this.dpr); canvas.height = Math.round(this.height * this.dpr);
      }
      this.cardBox = this.relative(this.card, section); this.actionBox = this.relative(this.action, section);
      const sign = this.relative(this.sign, section), scene = this.relative(this.scene, section);
      this.sceneBox = scene;
      const scale = Math.max(scene.width / 1672, scene.height / 941);
      const photoY = scene.y + (scene.height - 941 * scale) / 2;
      this.photoY = photoY; this.photoHeight = 941 * scale;
      this.horizonY = photoY + .507 * 941 * scale;
      this.skyFloorY = photoY + .385 * 941 * scale;
      this.shoreY = photoY + .812 * 941 * scale;
      if (sign?.width && sign?.height) {
        this.target = {x: sign.x + sign.width * .09, y: sign.y + sign.height * .17};
        const phone = this.width < 700;
        this.origin = phone
          ? {x: Math.max(16, this.target.x - 76), y: Math.max(14, Math.min(24, (this.cardBox?.y || 29) - 8))}
          : {x: Math.max(28, Math.min(this.target.x - 105, (this.cardBox?.x || this.target.x) - 28)),
            y: Math.max(24, this.target.y - 115)};
        this.boltPath = this.makeBolt(this.origin, this.target);
      } else { this.target = this.origin = this.boltPath = null; }
      this.pendingStill = true; this.paint();
    }

    levels() {
      const storm = smooth(.46, .555, this.phase) * (1 - smooth(.80, .91, this.phase));
      const rain = smooth(.57, .60, this.phase) * (1 - smooth(.78, .90, this.phase));
      const allowed = !this.reduced() && !this.lamp.reduced && this.lamp.starting;
      const primary = allowed && this.lamp.age < 1.22 ? this.lamp.strike : 0;
      const sheet = allowed && this.lamp.age >= 1.22 ? this.lamp.strike * .14 : 0;
      return {storm, rain, flash: clamp(Math.max(primary, sheet) * storm), bolt: primary * storm};
    }
    exclude(ctx, box, margin = 0, radius = 0) {
      if (!box) return;
      ctx.beginPath(); ctx.rect(0, 0, this.width, this.height);
      if (radius && typeof ctx.roundRect === 'function') {
        ctx.roundRect(box.x - margin, box.y - margin, box.width + margin * 2, box.height + margin * 2, radius);
      } else ctx.rect(box.x - margin, box.y - margin, box.width + margin * 2, box.height + margin * 2);
      ctx.clip('evenodd');
    }
    paint() {
      if (!this.drawable()) { this.pendingStill = true; return; }
      const {storm, rain, flash, bolt} = this.levels();
      const ctx = this.ctx, fx = this.boltCtx;
      for (const context of [ctx, fx]) {
        context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        context.clearRect(0, 0, this.width, this.height);
      }
      this.paintClouds(ctx, storm, flash);
      let dropCount = 0, impactCount = 0;
      this.section.dataset.weatherRainBatches = '0';
      this.section.dataset.weatherMistTiles = '0';
      this.section.dataset.weatherIslandDrops = '0';
      if (rain > .005) {
        // Match the visible card exactly: an expanded rectangular hole would
        // leave a second hard-edged dark frame in the surrounding rain haze.
        ctx.save(); this.exclude(ctx, this.cardBox, 0, this.width < 700 ? 16 : 18);
        this.paintCurtain(ctx, rain);
        dropCount = this.paintRain(ctx, rain);
        impactCount = this.paintImpacts(ctx, rain);
        ctx.restore();
      }
      if (bolt > .01 && this.boltPath) {
        fx.save(); this.exclude(fx, this.actionBox, 12); this.paintBolt(fx, bolt); fx.restore();
      }
      const data = this.section.dataset;
      data.weatherRenderer = 'canvas2d'; data.weatherPhase = this.phase.toFixed(5);
      data.weatherCloud = this.clouds.photographic ? 'photographic' : 'procedural-fallback';
      data.weatherStorm = storm.toFixed(3); data.weatherRain = rain.toFixed(3); data.weatherFlash = flash.toFixed(3);
      data.weatherBolt = bolt > .01 && this.boltPath ? 'cloud-to-sign' : 'none';
      data.weatherDrops = String(dropCount); data.weatherTime = this.elapsed.toFixed(3); data.weatherFrames = String(++this.frames);
      data.weatherRainLayers = '3'; data.weatherImpacts = String(impactCount);
      if (this.origin && this.target) {
        data.weatherOriginX = this.origin.x.toFixed(1); data.weatherOriginY = this.origin.y.toFixed(1);
        data.weatherTargetX = this.target.x.toFixed(1); data.weatherTargetY = this.target.y.toFixed(1);
      }
      const weatherKey = [data.weatherStorm, data.weatherRain, data.weatherFlash].join('/');
      if (weatherKey !== this.lastWeather) {
        this.lastWeather = weatherKey;
        this.scene.dispatchEvent(new CustomEvent('coast:weather', {detail: {storm, rain, flash}}));
      }
      this.pendingStill = false;
    }

    paintClouds(ctx, storm, flash) {
      const time = this.elapsed, w = this.width;
      // The whole cloud body stays above the island's highest ridge, including
      // the sign's foreground cloud and narrow phone crops.
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, w, Math.max(0, this.skyFloorY)); ctx.clip();
      const sunlit = (1 - this.night) * (1 - storm * .88);
      if (sunlit > .01) {
        for (let i = 0; i < 2; i++) {
          const width = Math.max(210, Math.min(440, w * .30));
          const x = ((time * (2.0 + i * .35) + w * (.08 + i * .56)) % (w + width)) - width * .2;
          const y = this.skyFloorY - width * .40 - 12 - i * 23;
          ctx.globalAlpha = sunlit * (.38 - i * .07);
          ctx.drawImage(this.clouds.day, x, y, width, width * .40);
        }
      }
      if (storm > .005) {
        const enter = smooth(.46, .565, this.phase), leave = smooth(.80, .91, this.phase);
        const width = Math.max(780, w * 1.23);
        const height = width * (.40 + Math.sin(time * .085) * .010);
        // The repeated broad bank advects continuously, even in a held night
        // mode. Overlap hides texture edges; the matching wrap has no jump.
        const period = width * .76, speed = clamp(w / 160, 4.5, 9);
        const flow = time * speed % period;
        const x = flow - period + (1 - enter) * w * .77 - leave * w * .30;
        const y = this.skyFloorY - height - 8;
        const draw = (image, left, top, breadth, tall, alpha, illuminated = false) => {
          ctx.save(); ctx.globalAlpha = alpha;
          if (illuminated) ctx.globalCompositeOperation = 'screen';
          ctx.drawImage(image, left, top, breadth, tall); ctx.restore();
        };
        // Include every tile intersecting the viewport. Drawing only two tiles
        // makes an overlapping tail disappear when the flow wraps.
        const first = x - Math.ceil((x + width) / period) * period;
        let tiles = 0;
        for (let left = first; left < w; left += period) {
          if (left + width <= 0) continue;
          draw(this.clouds.storm, left, y, width, height, storm * .97);
          if (flash > .002) draw(this.clouds.lit || this.clouds.day, left, y, width, height, flash * .83, true);
          tiles++;
        }
        this.section.dataset.weatherCloudFlow = flow.toFixed(2);
        this.section.dataset.weatherCloudSpeed = speed.toFixed(2);
        this.section.dataset.weatherCloudHeight = height.toFixed(2);
        this.section.dataset.weatherCloudPeriod = period.toFixed(2);
        this.section.dataset.weatherCloudTiles = String(tiles);
        if (this.origin) {
          const nearWidth = this.width < 700 ? 365 : 660;
          const nearHeight = nearWidth * (.40 + Math.sin(time * .11 + 2) * .016);
          const nearX = this.origin.x - nearWidth * .49 + (1 - enter) * w * .28
            + Math.sin(time * .095) * nearWidth * .075;
          const nearY = Math.min(this.origin.y - nearHeight * .43 + Math.sin(time * .13) * 5, this.skyFloorY - nearHeight - 6);
          draw(this.clouds.storm, nearX, nearY, nearWidth, nearHeight, storm * .96);
          if (flash > .002) draw(this.clouds.lit || this.clouds.day, nearX, nearY, nearWidth, nearHeight, flash * .98, true);
        }
      }
      ctx.restore(); ctx.globalAlpha = 1;
    }
    paintCurtain(ctx, rain) {
      // Rain haze occupies the island's air volume, rather than beginning at a
      // straight line on the sea. Two differently drifting, overlapping banks
      // reveal and obscure the ridge gradually. No per-frame pixel/filter work.
      const width = Math.max(560, this.width * .92), period = width * .60;
      const flow = this.elapsed * 7 % period;
      let tiles = 0;
      ctx.save();
      for (let layer = 0; layer < 2; layer++) {
        const tall = this.photoHeight * (layer ? .21 : .45);
        const centre = this.photoY + this.photoHeight * (layer ? .515 : .475);
        const offset = layer ? -(this.elapsed * 4.06 % period) + period * .35 : flow;
        const start = offset - Math.ceil((offset + width) / period) * period;
        ctx.globalAlpha = rain * (layer ? .24 : .66);
        for (let left = start; left < this.width; left += period) {
          if (left + width <= 0) continue;
          const y = centre - tall * .5 + Math.sin(this.elapsed * .13 + layer * 2) * tall * .045;
          ctx.drawImage(this.clouds.mist, left, y, width, tall); tiles++;
        }
      }
      ctx.restore();
      const data = this.section.dataset;
      data.weatherMistTiles = String(tiles); data.weatherMistFlow = flow.toFixed(2);
      data.weatherMistTop = (this.photoY + this.photoHeight * .23).toFixed(1);
      data.weatherMistBottom = (this.photoY + this.photoHeight * .72).toFixed(1);
    }
    paintRain(ctx, rain) {
      const top = Math.max(18, Math.min(this.horizonY - 95, this.sceneBox.y + 65));
      const span = this.height - top + 100;
      const limit = Math.round(this.drops.length * clamp(this.width / 1440, .5, 1));
      const gust = Math.sin(this.elapsed * .31) * .025 + Math.sin(this.elapsed * .71) * .012;
      const travelWidth = this.width + 260;
      for (const bucket of this.rainBuckets) bucket.length = 0;
      let count = 0, islandCount = 0;
      for (let index = 0; index < limit; index++) {
        const drop = this.drops[index];
        if (hash(index + 229) > rain) continue;
        const layer = RAIN_LAYERS[drop.layer];
        const speed = drop.speed * layer.speed;
        const y = top + ((drop.y * span + this.elapsed * speed) % span) - 70;
        const x = (drop.x * travelWidth + this.elapsed * (17 + drop.layer * 4) + (y - top) * (drop.lean + gust)) % travelWidth - 130;
        // The island is seen through nearby rain as well as distant rain.
        // Keep that foreground air legible above the horizon; depth still
        // gives the beach longer, faster streaks without hiding the storm aloft.
        const depth = smooth(this.horizonY - 40, this.shoreY + 90, y);
        drop.screenX = x; drop.screenY = y;
        drop.drawLength = drop.length * (.57 + .43 * depth * depth) * layer.length;
        drop.drawLean = drop.lean + gust;
        this.rainBuckets[drop.layer * 6 + Math.min(5, Math.floor(depth * 6))].push(drop);
        count++;
        if (x >= 0 && x <= this.width && y >= this.skyFloorY && y <= this.horizonY) islandCount++;
      }
      let batches = 0;
      for (let bucket = 0; bucket < this.rainBuckets.length; bucket++) {
        const drops = this.rainBuckets[bucket]; if (!drops.length) continue;
        const layer = RAIN_LAYERS[Math.floor(bucket / 6)], depth = (bucket % 6 + .5) / 6;
        const alpha = rain * (.18 + .09 * depth) * layer.alpha;
        ctx.strokeStyle = `rgba(${layer.rgb},${alpha})`;
        ctx.lineWidth = (.68 + .39 * depth) * layer.width;
        ctx.beginPath();
        for (const drop of drops) {ctx.moveTo(drop.screenX, drop.screenY);ctx.lineTo(drop.screenX + drop.drawLength * drop.drawLean, drop.screenY + drop.drawLength);}
        ctx.stroke();
        batches++;
      }
      this.section.dataset.weatherRainBatches = String(batches);
      this.section.dataset.weatherIslandDrops = String(islandCount);
      return count;
    }
    paintImpacts(ctx, rain) {
      let count = 0;
      const limit = this.width < 700 ? 34 : this.impacts.length;
      for (let index = 0; index < limit; index++) {
        const seed = this.impacts[index], age = (this.elapsed * seed.speed + seed.phase) % 1;
        if (age > .27 || hash(index + 2999) > rain) continue;
        const progress = age / .27, depth = .38 + seed.depth * .62;
        const x = seed.x * this.width, y = this.horizonY + (this.shoreY - this.horizonY) * depth;
        const radius = (.5 + 3.1 * depth) * (.35 + progress * .85);
        ctx.lineWidth = .45 + depth * .35;
        ctx.strokeStyle = `rgba(190,208,222,${rain * (1 - progress) * (.08 + depth * .15)})`;
        ctx.beginPath();ctx.ellipse(x, y, radius, radius * .24, 0, 0, TAU);ctx.stroke();
        count++;
      }
      return count;
    }
    makeBolt(origin, target) {
      const dx = target.x - origin.x, dy = target.y - origin.y, length = Math.max(.001, Math.hypot(dx, dy));
      return Array.from({length: 12}, (_, i) => {
        const t = i / 11, jitter = (hash(i + 209) - .5) * length * .13 * Math.sin(t * Math.PI);
        return {x: origin.x + dx * t - dy / length * jitter, y: origin.y + dy * t + dx / length * jitter};
      });
    }
    strokePath(ctx, points) {
      ctx.beginPath(); ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      ctx.stroke();
    }
    paintBolt(ctx, intensity) {
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      for (const [width, colour, alpha] of [[9, '104,139,255', .15], [4.2, '157,195,255', .54], [1.35, '247,251,255', .96]]) {
        ctx.lineWidth = width; ctx.strokeStyle = `rgba(${colour},${intensity * alpha})`; this.strokePath(ctx, this.boltPath);
      }
      for (const index of [3, 6]) {
        const root = this.boltPath[index];
        const branch = [{x: root.x, y: root.y}, {x: root.x - 17, y: root.y + 7},
          {x: root.x - 29, y: root.y + 4}, {x: root.x - 43, y: root.y + 16}];
        ctx.lineWidth = .75; ctx.strokeStyle = `rgba(198,222,255,${intensity * .51})`; this.strokePath(ctx, branch);
      }
      const glow = ctx.createRadialGradient(this.target.x, this.target.y, 0, this.target.x, this.target.y, 22);
      glow.addColorStop(0, `rgba(235,246,255,${intensity * .40})`);
      glow.addColorStop(.24, `rgba(131,191,255,${intensity * .16})`);
      glow.addColorStop(1, 'rgba(104,168,255,0)');
      ctx.fillStyle = glow; ctx.fillRect(this.target.x - 22, this.target.y - 22, 44, 44);
    }
    suspend() { this.suspended = true; this.sync(); }
    resume() { this.suspended = false; this.pendingStill = true; this.resize(); this.sync(); }
    destroy() {
      if (this.destroyed) return;
      this.destroyed = true; cancelAnimationFrame(this.frame); this.frame = 0;
      this.io?.disconnect(); this.ro?.disconnect(); this.cleanups.forEach(remove => remove());
      this.canvas.remove(); this.lightning.remove(); registry.delete(this.section);
    }
  }

  function scan(root = document) {
    const sections = [...root.querySelectorAll('[data-coastal-section]')];
    if (root.matches?.('[data-coastal-section]')) sections.unshift(root);
    for (const section of sections) {
      if (registry.has(section)) continue;
      const weather = new CoastalWeather(section);
      if (weather.ctx && weather.boltCtx) registry.set(section, weather);
    }
  }
  window.CgauCoastalWeather = {scan, destroyAll: () => [...registry.values()].forEach(instance => instance.destroy())};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), {once: true});
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [section, weather] of registry) if (event.target === section || event.target.contains(section)) weather.destroy();
  });
  window.addEventListener('pagehide', event => {
    for (const weather of [...registry.values()]) event.persisted ? weather.suspend() : weather.destroy();
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { for (const weather of registry.values()) weather.resume(); scan(); }
  });
})();
